import { Anchor } from "@components/Anchor";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { Body } from "@components/Typography";
import {
  ALPACA_BANK_SWEEP_PROGRAM_TERMS_URL,
  ALPACA_CUSTOMER_AGREEMENT_URL,
  ALTRUIST_ADVISOR_DISCLOSURES_URL_PART1,
  ALTRUIST_ADVISOR_DISCLOSURES_URL_PART2A,
  ALTRUIST_ADVISOR_DISCLOSURES_URL_PART3,
  ALTRUIST_CUSTOMER_AGREEMENTS_AND_DISCLOSURES_URL,
  ALTRUIST_PARTNER_BANKS_URL,
  FUTURE_MONEY_INVESTMENT_MANAGEMENT_AGREEMENT_URL,
  FUTURE_MONEY_TERMS_OF_SERVICE_URL,
} from "@constants";
import { StyledOrderedList, StyledUnorderedList } from "./styles";
import { TermsAndConditionsProps } from "./types";

export const TermsAndConditions = ({
  TypographyComponent = Body,
  textStyle,
}: TermsAndConditionsProps) => {
  return (
    <div>
      <TypographyComponent style={textStyle}>
        I acknowledge that FutureMoney RIA, LLC has relationships with multiple custodians, and will
        select a custodian for each of my accounts.
      </TypographyComponent>

      <Spacing size={SpacingSizes.M} />

      <TypographyComponent style={textStyle}>
        I have read, understood, and agree to be bound by Alpaca Securities LLC and FutureMoney RIA,
        LLC account terms, and all other terms, disclosures and disclaimers applicable to me, as
        referenced in the{" "}
        <Anchor href={ALPACA_CUSTOMER_AGREEMENT_URL}>Alpaca Customer Agreement</Anchor>.{" "}
        <b>
          I also acknowledge that the Alpaca Customer Agreement contains a pre-dispute arbitration
          clause in Section 44.
        </b>
      </TypographyComponent>

      <Spacing size={SpacingSizes.M} />

      <TypographyComponent style={textStyle}>
        For Traditional IRAs held with Alpaca Securities LLC, I appoint Equity Trust Company to
        serve as Trustee. By making the appointment, I agree to and acknowledge the following:
      </TypographyComponent>

      <StyledOrderedList>
        <li>
          <TypographyComponent style={textStyle}>
            I have read and understand the Individual Trust Agreement ("Trust Agreement"),
            Disclosure Statement, any applicable Schedule of Fees, and have reviewed the Privacy
            Notice, I agree to abide by the terms of the IRA documents listed above and confirm all
            information above is accurate.
          </TypographyComponent>
        </li>
        <li>
          <TypographyComponent style={textStyle}>
            I agree to pay all applicable fees described in the Schedule of Trustee Fees, which may
            be changed from time to time unless my brokerage firm is paying these fees on my behalf.
            If I do not pay such Trustee fees directly, I authorize my investment representative as
            "custodian" to debit such Trustee fees from my retirement plan account in accordance
            with the Trust Agreement.
          </TypographyComponent>
        </li>
        <li>
          <TypographyComponent style={textStyle}>
            I understand Equity Trust Company is not an investment advisor and does not supervise or
            control my investment representative. Equity Trust Company does not endorse any
            particular investment. I agree to use independent judgment in making my investment
            decisions.
          </TypographyComponent>
        </li>
        <li>
          <TypographyComponent style={textStyle}>
            I understand that this is a self-directed account and that I am solely responsible for
            selecting and managing my investments. I understand that neither Equity Trust, nor their
            agent, has a duty to question my direction (or the direction of any investment manager
            if one has been appointed for my account in accordance with the Trust Agreement) to
            purchase any investment in the self-directed brokerage account, to make suggestions
            regarding the investment, retention, or disposition of any assets held in the account,
            or to monitor any asset on an ongoing basis.
          </TypographyComponent>
        </li>
        <li>
          <TypographyComponent style={textStyle}>
            I understand that Equity Trust does not conduct, and has not conducted, a due diligence
            review of any investment, the issuer or sponsor of such investment, or any officer,
            director, person, or entity affiliated with such investment. I further understand that
            Equity Trust does not review, and has not reviewed, the merits of any investment or
            account transaction or whether it is acceptable under the Employee Retirement Income
            Security Act of 1974 (ERISA), the Internal Revenue Code (IRC) or any other applicable
            laws, including securities laws.
          </TypographyComponent>
        </li>
        <li>
          <TypographyComponent style={textStyle}>
            I represent that I will obtain and read all pertinent information relating to my
            investments, as applicable (i.e. prospectus, annual reports). I understand that Equity
            Trust is not responsible for obtaining, providing or retaining this information.
          </TypographyComponent>
        </li>
        <li>
          <TypographyComponent style={textStyle}>
            I agree to consult with an attorney, tax advisor, and investment advisor to review the
            suitability of any investment I purchase in the self-directed account, if necessary.
          </TypographyComponent>
        </li>
        <li>
          <TypographyComponent style={textStyle}>
            I understand that certain transactions are prohibited under IRC §4975 and ERISA §406 and
            that I am responsible for ensuring that account investments or transactions do not
            constitute a prohibited transaction.
          </TypographyComponent>
        </li>
        <li>
          <TypographyComponent style={textStyle}>
            I understand that if an investment cannot be liquidated, it is my responsibility to
            ensure that I can satisfy any mandatory distribution requirements with other IRA
            investments.
          </TypographyComponent>
        </li>
        <li>
          <TypographyComponent style={textStyle}>
            I understand that Equity Trust is a non-depository trust company and will not hold
            negotiable certificates. I also understand that I cannot hold the certificates. I agree
            that, if an investment issues certificates, I will have established an account with a
            brokerage firm to hold the certificates and that I have verified with the brokerage firm
            that it can hold the certificates for this investment. I further agree that if I change
            brokerage firms it will be my responsibility to ensure the new firm can also hold the
            certificates and notify Equity Trust of the change.
          </TypographyComponent>
        </li>
        <li>
          <TypographyComponent style={textStyle}>
            I agree to pay all fees that may be charged by the brokerage firm or investment sponsor
            to liquidate and/or re-register the account in the event the Equity Trust resigns for
            any reason.
          </TypographyComponent>
        </li>
        <li>
          <TypographyComponent style={textStyle}>
            I hereby agree to indemnify and hold Equity Trust and its respective officers,
            directors, employees, agents, affiliates, successors, and assigns, harmless from, and
            against, any and all claims, liabilities, penalties, costs or expenses (including,
            without limitation, attorney fees and court and legal costs) of any nature whatsoever
            arising directly or indirectly by reason of, or resulting from, the purchase of the
            investment(s).
          </TypographyComponent>
        </li>
        <li>
          <TypographyComponent style={textStyle}>
            Upon an IRA account opened at Equity Trust, your application will be deemed accepted and
            Equity Trust will be appointed as trustee.
          </TypographyComponent>
        </li>
      </StyledOrderedList>

      <Spacing size={SpacingSizes.M} />

      <TypographyComponent style={textStyle}>
        For Roth IRAs held with Alpaca Securities LLC, I appoint Equity Trust Company to serve as
        Trustee. By making the appointment, I agree to and acknowledge the following:
      </TypographyComponent>

      <StyledOrderedList>
        <li>
          <TypographyComponent style={textStyle}>
            I have read and understand the Individual Trust Agreement ("Trust Agreement"),
            Disclosure Statement, any applicable Schedule of Fees, and have reviewed the Privacy
            Notice, I agree to abide by the terms of the IRA documents listed above and confirm all
            information above is accurate.
          </TypographyComponent>
        </li>
        <li>
          <TypographyComponent style={textStyle}>
            I agree to pay all applicable fees described in the Schedule of Trustee Fees, which may
            be changed from time to time unless my brokerage firm is paying these fees on my behalf.
            If I do not pay such Trustee fees directly, I authorize my investment representative as
            "custodian" to debit such Trustee fees from my retirement plan account in accordance
            with the Trust Agreement.
          </TypographyComponent>
        </li>
        <li>
          <TypographyComponent style={textStyle}>
            I understand Equity Trust Company is not an investment advisor and does not supervise or
            control my investment representative. Equity Trust Company does not endorse any
            particular investment. I agree to use independent judgment in making my investment
            decisions.
          </TypographyComponent>
        </li>
        <li>
          <TypographyComponent style={textStyle}>
            I understand that this is a self-directed account and that I am solely responsible for
            selecting and managing my investments. I understand that neither Equity Trust, nor their
            agent, has a duty to question my direction (or the direction of any investment manager
            if one has been appointed for my account in accordance with the Trust Agreement) to
            purchase any investment in the self-directed brokerage account, to make suggestions
            regarding the investment, retention, or disposition of any assets held in the account,
            or to monitor any asset on an ongoing basis.
          </TypographyComponent>
        </li>
        <li>
          <TypographyComponent style={textStyle}>
            I understand that Equity Trust does not conduct, and has not conducted, a due diligence
            review of any investment, the issuer or sponsor of such investment, or any officer,
            director, person, or entity affiliated with such investment. I further understand that
            Equity Trust does not review, and has not reviewed, the merits of any investment or
            account transaction or whether it is acceptable under the Employee Retirement Income
            Security Act of 1974 (ERISA), the Internal Revenue Code (IRC) or any other applicable
            laws, including securities laws.
          </TypographyComponent>
        </li>
        <li>
          <TypographyComponent style={textStyle}>
            I represent that I will obtain and read all pertinent information relating to my
            investments, as applicable (i.e. prospectus, annual reports). I understand that Equity
            Trust is not responsible for obtaining, providing or retaining this information.
          </TypographyComponent>
        </li>
        <li>
          <TypographyComponent style={textStyle}>
            I agree to consult with an attorney, tax advisor, and investment advisor to review the
            suitability of any investment I purchase in the self-directed account, if necessary.
          </TypographyComponent>
        </li>
        <li>
          <TypographyComponent style={textStyle}>
            I understand that certain transactions are prohibited under IRC §4975 and ERISA §406 and
            that I am responsible for ensuring that account investments or transactions do not
            constitute a prohibited transaction.
          </TypographyComponent>
        </li>
        <li>
          <TypographyComponent style={textStyle}>
            I understand that if an investment cannot be liquidated, it is my responsibility to
            ensure that I can satisfy any mandatory distribution requirements with other IRA
            investments.
          </TypographyComponent>
        </li>
        <li>
          <TypographyComponent style={textStyle}>
            I understand that Equity Trust is a non-depository trust company and will not hold
            negotiable certificates. I also understand that I cannot hold the certificates. I agree
            that, if an investment issues certificates, I will have established an account with a
            brokerage firm to hold the certificates and that I have verified with the brokerage firm
            that it can hold the certificates for this investment. I further agree that if I change
            brokerage firms it will be my responsibility to ensure the new firm can also hold the
            certificates and notify Equity Trust of the change.
          </TypographyComponent>
        </li>
        <li>
          <TypographyComponent style={textStyle}>
            I agree to pay all fees that may be charged by the brokerage firm or investment sponsor
            to liquidate and/or re-register the account in the event the Equity Trust resigns for
            any reason.
          </TypographyComponent>
        </li>
        <li>
          <TypographyComponent style={textStyle}>
            I hereby agree to indemnify and hold Equity Trust and its respective officers,
            directors, employees, agents, affiliates, successors, and assigns, harmless from, and
            against, any and all claims, liabilities, penalties, costs or expenses (including,
            without limitation, attorney fees and court and legal costs) of any nature whatsoever
            arising directly or indirectly by reason of, or resulting from, the purchase of the
            investment(s).
          </TypographyComponent>
        </li>
        <li>
          <TypographyComponent style={textStyle}>
            Upon an IRA account opened at Equity Trust, your application will be deemed accepted and
            Equity Trust will be appointed as trustee.
          </TypographyComponent>
        </li>
      </StyledOrderedList>

      <Spacing size={SpacingSizes.M} />

      <TypographyComponent style={textStyle}>
        I acknowledge that I have been presented the{" "}
        <Anchor href={ALPACA_BANK_SWEEP_PROGRAM_TERMS_URL}>
          Alpaca Securities LLC Bank Sweep Program Terms and Conditions
        </Anchor>
        .
      </TypographyComponent>

      <Spacing size={SpacingSizes.M} />

      <CollegeAmericaTermsSection TypographyComponent={TypographyComponent} textStyle={textStyle} />

      <Spacing size={SpacingSizes.M} />

      <TypographyComponent style={textStyle}>
        I have reviewed and accepted the agreements and disclosures for my account with Altruist
        Financial LLC. I acknowledge that I will be providing an electronic signature to open my
        account.{" "}
        <b>
          I understand that the Altruist Financial LLC Customer Agreement contains a predispute
          arbitration clause in Section 27.
        </b>
      </TypographyComponent>
      <StyledUnorderedList>
        <li>
          <TypographyComponent style={textStyle}>
            I agree to FutureMoney's{" "}
            <Anchor href={FUTURE_MONEY_TERMS_OF_SERVICE_URL}>Terms and Conditions</Anchor>
          </TypographyComponent>
        </li>
        <li>
          <TypographyComponent style={textStyle}>
            I agree to FutureMoney's{" "}
            <Anchor href={FUTURE_MONEY_INVESTMENT_MANAGEMENT_AGREEMENT_URL}>
              Investment Management Agreement
            </Anchor>
          </TypographyComponent>
        </li>
        <li>
          <TypographyComponent style={textStyle}>
            I agree to and acknowledge the{" "}
            <Anchor href={ALTRUIST_CUSTOMER_AGREEMENTS_AND_DISCLOSURES_URL}>
              Altruist customer agreements and disclosures
            </Anchor>
          </TypographyComponent>
        </li>
        <li>
          <TypographyComponent style={textStyle}>
            I acknowledge my advisor's disclosures (
            <Anchor href={ALTRUIST_ADVISOR_DISCLOSURES_URL_PART1}>Part 1</Anchor>,{" "}
            <Anchor href={ALTRUIST_ADVISOR_DISCLOSURES_URL_PART2A}>2A</Anchor>,{" "}
            <Anchor href={ALTRUIST_ADVISOR_DISCLOSURES_URL_PART3}>3</Anchor>)
          </TypographyComponent>
        </li>
        <li>
          <TypographyComponent style={textStyle}>
            I agree to the{" "}
            <Anchor href={ALTRUIST_CUSTOMER_AGREEMENTS_AND_DISCLOSURES_URL}>
              Altruist Roth Individual Retirement Custodial Account Agreement
            </Anchor>
          </TypographyComponent>
        </li>
        <li>
          <TypographyComponent style={textStyle}>
            I agree to the{" "}
            <Anchor href={ALTRUIST_CUSTOMER_AGREEMENTS_AND_DISCLOSURES_URL}>
              Altruist Electronic Delivery Consent Agreement
            </Anchor>
          </TypographyComponent>
        </li>
        <li>
          <TypographyComponent style={textStyle}>
            I agree to participate in Altruist's Fully Paid Lending Program, which provides the
            opportunity to earn additional monthly income on stock and ETF positions I already own.
          </TypographyComponent>
        </li>
        <li>
          <TypographyComponent style={textStyle}>
            I opt-in to Altruist's dividend reinvestment program ("DRIP") for stocks and ETFs
            (mutual funds are enrolled in the DRIP by default)
          </TypographyComponent>
        </li>
        <li>
          <TypographyComponent style={textStyle}>
            I agree to participate in Altruist's Bank Sweep Program, which enables my cash balances
            to earn interest while awaiting investment, through FDIC-insured deposits at{" "}
            <Anchor href={ALTRUIST_PARTNER_BANKS_URL}>partner banks</Anchor>.
          </TypographyComponent>
        </li>
      </StyledUnorderedList>

      <Spacing size={SpacingSizes.M} />

      <TypographyComponent style={textStyle}>
        I confirm that <b>none of the following apply</b> to me or a member of your immediate
        family:
      </TypographyComponent>

      <StyledUnorderedList>
        <li>
          <TypographyComponent style={textStyle}>
            Affiliated or work with a US registered broker-dealer or FINRA.
          </TypographyComponent>
        </li>
        <li>
          <TypographyComponent style={textStyle}>
            Senior executive at or a 10% or greater shareholder of a publicly traded company.
          </TypographyComponent>
        </li>
        <li>
          <TypographyComponent style={textStyle}>
            I am a senior political figure.
          </TypographyComponent>
        </li>
        <li>
          <TypographyComponent style={textStyle}>
            I am a family member or relative of a senior political figure.
          </TypographyComponent>
        </li>
      </StyledUnorderedList>

      <Spacing size={SpacingSizes.M} />

      <TypographyComponent style={textStyle}>
        A "Politically Exposed Person" (PEP) is defined by FATF as an individual who is or has been
        entrusted with a prominent public function, for example: Heads of State or of government,
        senior politicians, senior government, judicial or military officials, senior executives of
        state-owned corporations, or important political party officials.
      </TypographyComponent>

      <Spacing size={SpacingSizes.M} />

      <TypographyComponent style={textStyle}>
        I certify, under penalties of perjury, that the number shown on this application is my
        correct Taxpayer Identification Number, I am a U.S. person and FATCA reporting does not
        apply.
      </TypographyComponent>

      <Spacing size={SpacingSizes.M} />

      <TypographyComponent style={textStyle}>
        The Internal Revenue Service does not require your consent to any provision of this document
        other than the certifications required to avoid backup withholding.
      </TypographyComponent>

      <Spacing size={SpacingSizes.M} />
    </div>
  );
};

type CollegeAmericaTermsSectionProps = Pick<
  TermsAndConditionsProps,
  "TypographyComponent" | "textStyle"
>;

const CollegeAmericaTermsSection = ({
  TypographyComponent = Body,
  textStyle,
}: CollegeAmericaTermsSectionProps) => {
  return (
    <>
      <TypographyComponent style={textStyle}>
        <b>Agreement and Disclosures - CollegeAmerica® 529 Savings Plan</b>
      </TypographyComponent>

      <Spacing size={SpacingSizes.XS} />

      <TypographyComponent style={textStyle}>
        By clicking "Continue" or otherwise proceeding with this application via the FutureMoney
        mobile application or website, you acknowledge that you have read, understood, and agreed to
        the following terms and conditions governing the opening of your CollegeAmerica 529 account
        with Capital Group and American Funds Service Company (AFS).
      </TypographyComponent>

      <Spacing size={SpacingSizes.XS} />

      <TypographyComponent style={textStyle}>
        <b>1. ELECTRONIC SIGNATURE AND PAPERLESS CONSENT</b>
      </TypographyComponent>

      <Spacing size={SpacingSizes.XS} />

      <TypographyComponent style={textStyle}>
        By using this application, you consent to use electronic signatures to sign all required
        documents, including the CollegeAmerica Account Application. You acknowledge that your
        electronic signature is the legally binding equivalent of a handwritten signature.
        Furthermore, you agree to:
      </TypographyComponent>

      <StyledUnorderedList>
        <li>
          <TypographyComponent style={textStyle}>
            Receive all plan documents, including the CollegeAmerica Program Description,
            prospectuses, privacy notices, and tax forms (e.g., 1099-Q) electronically.
          </TypographyComponent>
        </li>
        <li>
          <TypographyComponent style={textStyle}>
            Maintain a valid email address and notify FutureMoney RIA, LLC immediately of any
            changes to your contact information.
          </TypographyComponent>
        </li>
        <li>
          <TypographyComponent style={textStyle}>
            Access documents provided in PDF format through the app or your designated email.
          </TypographyComponent>
        </li>
      </StyledUnorderedList>

      <Spacing size={SpacingSizes.XS} />

      <TypographyComponent style={textStyle}>
        <b>2. OUT-OF-STATE DISCLOSURE NOTICE</b>
      </TypographyComponent>

      <Spacing size={SpacingSizes.XS} />

      <TypographyComponent style={textStyle}>
        IMPORTANT: CollegeAmerica is sponsored by the Commonwealth of Virginia. If you are not a
        resident of Virginia, you should consider before investing whether your or the beneficiary's
        home state offers a 529 plan that provides its taxpayers with favorable state tax or other
        benefits that may only be available through investment in the home state's 529 plan. Such
        benefits may include financial aid, scholarship funds, and protection from creditors. You
        should consult a tax professional to understand how your state's laws apply to your
        investment.
      </TypographyComponent>

      <Spacing size={SpacingSizes.XS} />

      <TypographyComponent style={textStyle}>
        <b>3. INVESTMENT INSTRUCTION AND ROLE OF ADVISER</b>
      </TypographyComponent>

      <Spacing size={SpacingSizes.XS} />

      <TypographyComponent style={textStyle}>
        You authorize FutureMoney RIA, LLC to act as your agent in transmitting investment
        instructions to American Funds Service Company (AFS). You acknowledge that:
      </TypographyComponent>

      <StyledUnorderedList>
        <li>
          <TypographyComponent style={textStyle}>
            FutureMoney RIA, LLC is responsible for the selection of investment portfolios and the
            suitability of the investment strategy for your financial goals.
          </TypographyComponent>
        </li>
        <li>
          <TypographyComponent style={textStyle}>
            Capital Group and AFS act solely as the plan manager and transfer agent and are not
            responsible for the investment advice provided by FutureMoney RIA, LLC.
          </TypographyComponent>
        </li>
        <li>
          <TypographyComponent style={textStyle}>
            You have received and read the current CollegeAmerica Program Description and the
            prospectuses for the underlying mutual funds before making this investment.
          </TypographyComponent>
        </li>
      </StyledUnorderedList>

      <Spacing size={SpacingSizes.XS} />

      <TypographyComponent style={textStyle}>
        <b>4. BANKING AND ACH AUTHORIZATION</b>
      </TypographyComponent>

      <Spacing size={SpacingSizes.XS} />

      <TypographyComponent style={textStyle}>
        You attest that you authorize American Funds Service Company (AFS), upon request to secure
        payments from your designated bank account for amounts invested in your American Funds
        account(s) via Web, telephone, or any other means by any person with account information.
        You authorize your bank to accept any such debits to your account, without responsibility
        for their correctness.
      </TypographyComponent>

      <Spacing size={SpacingSizes.XS} />

      <TypographyComponent style={textStyle}>
        You understand that any shares purchased via ACH may not be available for sale or withdrawal
        for approximately 10 calendar days following the purchase date. You may terminate this
        authorization at any time by telephone or written notification to AFS, effective once AFS
        has had reasonable time to act upon it.
      </TypographyComponent>

      <Spacing size={SpacingSizes.XS} />

      <TypographyComponent style={textStyle}>
        <b>5. COMPLIANCE AND IDENTITY VERIFICATION</b>
      </TypographyComponent>

      <Spacing size={SpacingSizes.XS} />

      <TypographyComponent style={textStyle}>
        To help the government fight the funding of terrorism and money laundering activities,
        Federal law (USA PATRIOT Act) requires all financial institutions to obtain, verify, and
        record information that identifies each person who opens an account. You authorize
        FutureMoney RIA, LLC and AFS to verify your identity using the information provided, which
        may include your name, address, date of birth, and Social Security number. You understand
        that your account may be restricted or closed if your identity cannot be verified.
      </TypographyComponent>

      <Spacing size={SpacingSizes.XS} />

      <TypographyComponent style={textStyle}>
        <b>6. SYSTEMATIC CONTRIBUTIONS AND NSF POLICIES</b>
      </TypographyComponent>

      <Spacing size={SpacingSizes.XS} />

      <TypographyComponent style={textStyle}>
        If you establish a recurring contribution plan, you acknowledge that drafts will occur on
        the date specified. If a draft date falls on a weekend or holiday, the transaction will
        occur on the next business day at that day's Net Asset Value (NAV). In the event of
        insufficient funds (NSF) or a returned payment, AFS reserves the right to charge a fee
        and/or cancel the shares purchased, and you agree to be responsible for any resulting market
        loss.
      </TypographyComponent>

      <Spacing size={SpacingSizes.XS} />

      <TypographyComponent style={textStyle}>
        <b>7. INDEMNIFICATION</b>
      </TypographyComponent>

      <Spacing size={SpacingSizes.XS} />

      <TypographyComponent style={textStyle}>
        In consideration of AFS acting on such instructions and processing such transactions, you
        agree to hold harmless and indemnify AFS and any of its affiliates or mutual funds managed
        by such affiliates, each of their respective directors, trustees, officers, employees, and
        agents from any losses, expenses, costs, or liability (including attorney fees) that may be
        incurred as a result of AFS establishing these privileges or acting on such instructions.
      </TypographyComponent>

      <Spacing size={SpacingSizes.XS} />

      <TypographyComponent style={textStyle}>
        <b>8. RISKS AND PLAN TERMS</b>
      </TypographyComponent>

      <Spacing size={SpacingSizes.XS} />

      <TypographyComponent style={textStyle}>
        Investment in the CollegeAmerica plan involves risk, including the possible loss of
        principal. Neither the Commonwealth of Virginia, Virginia529, the Federal Deposit Insurance
        Corporation, nor Capital Group guarantees the investment return or the principal amount
        invested. You understand that tax treatment of 529 plans is subject to change by the IRS or
        state legislatures.
      </TypographyComponent>

      <Spacing size={SpacingSizes.XS} />

      <TypographyComponent style={textStyle}>
        CollegeAmerica is a Section 529 program established by the Commonwealth of Virginia.
        American Funds Distributors, Inc., member FINRA. Capital Group and CollegeAmerica are
        registered trademarks of The Capital Group Companies, Inc.
      </TypographyComponent>
    </>
  );
};
